import { Service } from '@angular/core';
import { Selector, State, StateContext } from '@ngxs/store';
import { CollectionDataServiceBase } from '@portal-core/data/collection/services/collection-data.service.base';
import { CollectionStateSelectors } from '@portal-core/data/collection/services/collection-state-selectors.decorator';
import { CollectionStateBase } from '@portal-core/data/collection/services/collection-state.base';
import { AddItems, RemoveItems } from '@portal-core/data/collection/types/collection-state.type';
import { TaskAsset } from '@portal-core/tasks/models/task-asset.model';
import { Observable, map } from 'rxjs';

@CollectionStateSelectors({
  stateType: TaskAssetsState
})
@State<TaskAssetsState>({
  name: TaskAssetsState.source,
})
@Service({ autoProvided: false })
export class TaskAssetsState extends CollectionStateBase {
  static override source = 'TaskAssets';
  ByTaskId: { [taskId: number]: number[] } = {};
  ByCommentId: { [commentId: number]: number[] } = {};

  getSource(): string {
    return TaskAssetsState.source;
  }

  override getAddItemsState(_ctx: StateContext<CollectionStateBase>, action: AddItems): this {
    let taskId: number;
    const assets = action.payload.items;
    for (const prop in assets) {
      if (assets[prop]
        && typeof assets[prop] !== 'function') {
        const asset = (<TaskAsset>assets[prop]);
        const commentId = asset.TaskCommentId;
        if (!taskId) {
          taskId = asset.TaskId;
        }

        this.updateAsset(asset, taskId, this.ByTaskId);
        this.updateAsset(asset, commentId, this.ByCommentId);
      }
    }
    return this;
  }

  override getRemoveItemsState(_ctx: StateContext<CollectionStateBase>, action: RemoveItems): this {
    const assets = action.payload.itemIds;
    for (const prop in assets) {
      if (assets[prop]
        && typeof assets[prop] !== 'function') {
        const assetId = (<number>assets[prop]);

        Object.entries(this.ByTaskId).forEach(([_key, value]) => {
          value.some(id => {
            if (id === assetId) {
              value.splice(value.indexOf(assetId), 1);
            }
            return id === assetId;
          });
        });
        Object.entries(this.ByCommentId).forEach(([_key, value]) => {
          value.some(id => {
            if (id === assetId) {
              value.splice(value.indexOf(assetId), 1);
            }
            return id === assetId;
          });
        });
      }
    }
    return this;
  }

  override getResetState(): this {
    this.ByCommentId = {};
    this.ByTaskId = {};
    return this;
  }

  updateAssets(assets: Dictionary<TaskAsset> | TaskAsset[], taskId: number) {
    if (Object.keys(assets).length > 0) {
      for (const asset of Object.values(assets)) {
        // Object.values yields only own enumerable values (never the prototype methods a for..in would
        // surface), so the previous typeof-function guard is unnecessary; the null/undefined guard is
        // kept because asset.TaskCommentId is read before updateAsset's own undefined check.
        if (!asset) {
          continue;
        }
        const commentId = asset.TaskCommentId;

        this.updateAsset(asset, taskId, this.ByTaskId);
        this.updateAsset(asset, commentId, this.ByCommentId);
      }
    } else {
      this.updateAsset(undefined, taskId, this.ByTaskId);
    }
  }

  private updateAsset(asset: TaskAsset, id: number, collection: { [id: number]: number[] }) {
    if (!(id in collection)) {
      collection[id] = [];
    }
    if (asset !== undefined
      && collection[id].indexOf(asset.Id) === -1) {
      collection[id].push(asset.Id);
    }
  }
}

@Service()
export class TaskAssetsDataService extends CollectionDataServiceBase<TaskAsset> {
  protected readonly stateType = TaskAssetsState;

  @Selector([TaskAssetsState])
  static getByTaskIdFn$(state: TaskAssetsState) {
    return (taskId: number) => {
      if (state.ByTaskId === undefined
        || !(taskId in state.ByTaskId)) {
        return undefined;
      }
      const assets = [];
      for (const prop in state.ByTaskId[taskId]) {
        if (state.ByTaskId[taskId][prop]
          && typeof (state.ByTaskId[taskId][prop]) !== 'function') {
          const asset = state.items[(state.ByTaskId[taskId][prop])];
          if (asset !== undefined) {
            assets.push(asset);
          }
        }
      }
      return assets;
    };
  }

  @Selector([TaskAssetsState])
  static getByCommentIdFn$(state: TaskAssetsState) {
    return (commentId: number) => {
      if (state.ByCommentId === undefined
        || !(commentId in state.ByCommentId)) {
        return undefined;
      }
      const assets = [];
      for (const prop in state.ByCommentId[commentId]) {
        if (state.ByCommentId[commentId][prop]
          && typeof (state.ByCommentId[commentId][prop]) !== 'function') {
          const asset = state.items[(state.ByCommentId[commentId][prop])];
          if (asset !== undefined) {
            assets.push(asset);
          }
        }
      }
      return assets;
    };
  }

  addAssets$(items: Dictionary<TaskAsset> | TaskAsset[]): Observable<void> {
    return super.addItems$(items);
  }

  getItemsByTaskId$(taskId: number): Observable<TaskAsset[]> {
    return this.store.select(TaskAssetsDataService.getByTaskIdFn$).pipe(map(getByTaskIdFn => getByTaskIdFn(taskId)));
  }

  getItemsByCommentId$(commentId: number): Observable<TaskAsset[]> {
    return this.store.select(TaskAssetsDataService.getByCommentIdFn$).pipe(map(getByCommentIdFn => getByCommentIdFn(commentId)));
  }
}
